import uPlot from 'uplot';
import { useEffect, useRef } from 'react';
import { useResolvedTheme } from '@/hooks/useTheme';

interface ConvergenceChartProps {
  values: number[];
}

/** A small live line chart of the optimizer's best-value-so-far, redrawn as new evaluations arrive from polling. */
export function ConvergenceChart({ values }: ConvergenceChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  const resolvedTheme = useResolvedTheme();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const axisColor = resolvedTheme === 'dark' ? '#8b97a8' : '#5a6472';
    const gridColor = resolvedTheme === 'dark' ? 'rgba(139, 151, 168, 0.16)' : 'rgba(90, 100, 114, 0.16)';
    const seriesColor = resolvedTheme === 'dark' ? '#4fa3ff' : '#1d6fd0';

    const plot = new uPlot(
      {
        width: container.clientWidth,
        height: container.clientHeight,
        series: [{ label: 'Evaluation' }, { label: 'Best value', stroke: seriesColor, width: 1.5 }],
        axes: [
          { stroke: axisColor, grid: { stroke: gridColor } },
          { stroke: axisColor, grid: { stroke: gridColor } },
        ],
        scales: { x: { time: false } },
        legend: { show: false },
      },
      [values.map((_, i) => i), values],
      container,
    );
    plotRef.current = plot;

    const resizeObserver = new ResizeObserver(() => plot.setSize({ width: container.clientWidth, height: container.clientHeight }));
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      plot.destroy();
      plotRef.current = null;
    };
  }, [resolvedTheme]);

  useEffect(() => {
    plotRef.current?.setData([values.map((_, i) => i), values]);
  }, [values]);

  return <div ref={containerRef} className="h-full w-full" />;
}
